import {
  SkyButton,
  SkyCard,
  SkyDialog,
  SkyInput,
  SkyMessageBar,
  SkySwitch,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { McpGrantListSection } from "@/components/mcp/McpGrantList";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  MCP_REVOKE_ALL_CONFIRMATION,
  useInstanceMcpGrants,
  useInstanceMcpSwitch,
  useRevokeAllMcpGrants,
  useSetInstanceMcpSwitch,
  useSetTenantMcpSwitch,
  useTenantMcpGrants,
  useTenantMcpSwitch,
} from "@/hooks/use-mcp";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { MCP_APPROVE_TABLE_THRESHOLD, MCP_WRITES_PER_HOUR } from "@shared/mcp/writeIntents";

/** What the tenant's write toggle allows, and what still bounds it (ADR 0009 section 6). */
export const TENANT_WRITE_DESCRIPTION = `Lets each person choose, when they connect an app, to allow it to make changes as them: create a draft connection, change schedules, start runs. Enabling or disabling a connection, anything that imports more than ${MCP_APPROVE_TABLE_THRESHOLD} tables, and every change after ${MCP_WRITES_PER_HOUR} in an hour wait for that person's approval in skyData. Credentials are always typed in skyData, never by an app. Off, apps only read. Nobody is disconnected.`;

/**
 * AI apps (ADR 0009 sections 4, 6 and 9): who connected which AI app, Disconnect, and the
 * switches below the platform flag. Each section shows for its own permission key, never a role
 * name:
 * - This instance: the off switch (`instance.settings.write`) and the instance's connections
 *   (`instance.users.manage`).
 * - This tenant: the read and write toggles (`tenant.instances.manage`) and every connection in
 *   the tenant (`tenant.users.manage`).
 * - Every tenant: Disconnect every app (`platform.settings.manage`, typed confirmation).
 *
 * Turning a switch off disconnects nothing: the apps are refused (`MCP_OFF`) until it is on
 * again. The platform flag itself is in Platform settings. Release 8B makes the tenant's write
 * toggle usable (off by default): while it is off, no app makes a change in the tenant, whatever
 * a person ticked at consent, and no grant changes either way.
 */

function Section({
  title,
  intro,
  testId,
  children,
}: {
  title: string;
  intro: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3" data-testid={testId}>
      <div>
        <SkyText as="h2" size={400} weight="semibold" block>
          {title}
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-1">
          {intro}
        </SkyText>
      </div>
      {children}
    </section>
  );
}

function PlatformOffNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      testId="ai-apps-platform-off"
      title="AI apps are off for this deployment"
      message="Nothing below takes effect until a platform administrator turns on AI apps in Platform settings."
    />
  );
}

function InstanceSwitchCard({ instanceName }: { instanceName: string }) {
  const view = useInstanceMcpSwitch();
  const save = useSetInstanceMcpSwitch();
  const notice = useNotice();
  const saveError = useInlineError();
  const data = view.data;
  if (!data) {
    return (
      <SkyText size={300} color="muted" testId="ai-apps-instance-switch-loading">
        {view.isError ? "The switch did not load. Reload the page to try again." : "Loading…"}
      </SkyText>
    );
  }
  const tenantOff = !data.tenantReadEnabled;
  return (
    <SkyCard className="flex flex-col gap-3 p-4">
      <PlatformOffNote show={!data.platformEnabled} />
      <SkySwitch
        label={`AI apps can read ${instanceName}`}
        description="Off, every connected app is refused here until you turn it on again. Nobody is disconnected."
        checked={data.enabled}
        disabled={save.isPending}
        onCheckedChange={(on) => {
          saveError.clear();
          save.mutate(
            { enabled: on },
            {
              onSuccess: () =>
                notice.success(
                  on
                    ? `AI apps can read ${instanceName} again.`
                    : `AI apps are off for ${instanceName}.`,
                ),
              onError: (error) => saveError.show(error, "The switch was not changed."),
            },
          );
        }}
        testId="ai-apps-instance-switch"
      />
      {tenantOff ? (
        <SkyText as="p" size={200} color="muted" block testId="ai-apps-instance-tenant-off">
          Your tenant has turned AI apps off, so they stay off here whatever this switch says.
        </SkyText>
      ) : null}
      <InlineErrorBar
        error={saveError.error}
        title="The switch was not changed"
        onDismiss={saveError.clear}
        testId="ai-apps-instance-switch-error"
      />
    </SkyCard>
  );
}

function TenantSwitchCard({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const view = useTenantMcpSwitch(tenantId);
  const save = useSetTenantMcpSwitch(tenantId);
  const notice = useNotice();
  const saveError = useInlineError();
  const data = view.data;
  if (!data) {
    return (
      <SkyText size={300} color="muted" testId="ai-apps-tenant-switch-loading">
        {view.isError ? "The switches did not load. Reload the page to try again." : "Loading…"}
      </SkyText>
    );
  }
  return (
    <SkyCard className="flex flex-col gap-3 p-4">
      <PlatformOffNote show={!data.platformEnabled} />
      <SkySwitch
        label={`AI apps can read ${tenantName}`}
        description="Off, every connected app is refused in every instance of this tenant until you turn it on again. Nobody is disconnected."
        checked={data.readEnabled}
        disabled={save.isPending}
        onCheckedChange={(on) => {
          saveError.clear();
          save.mutate(
            { readEnabled: on, writeEnabled: on ? data.writeEnabled : false },
            {
              onSuccess: () =>
                notice.success(
                  on
                    ? `AI apps can read ${tenantName} again.`
                    : `AI apps are off for ${tenantName}.`,
                ),
              onError: (error) => saveError.show(error, "The switch was not changed."),
            },
          );
        }}
        testId="ai-apps-tenant-read"
      />
      <SkySwitch
        label="AI apps can make changes"
        description={
          data.writeAvailable
            ? TENANT_WRITE_DESCRIPTION
            : "Not available yet: AI apps can only read in this release. This stays off until changes are offered."
        }
        checked={data.writeEnabled}
        disabled={!data.writeAvailable || !data.readEnabled || save.isPending}
        onCheckedChange={(on) => {
          saveError.clear();
          save.mutate(
            { readEnabled: data.readEnabled, writeEnabled: on },
            {
              onSuccess: () =>
                notice.success(
                  on
                    ? `AI apps can make changes in ${tenantName} for people who allow it.`
                    : `AI apps can only read in ${tenantName} now.`,
                ),
              onError: (error) => saveError.show(error, "The switch was not changed."),
            },
          );
        }}
        testId="ai-apps-tenant-write"
      />
      <InlineErrorBar
        error={saveError.error}
        title="The switch was not changed"
        onDismiss={saveError.clear}
        testId="ai-apps-tenant-switch-error"
      />
    </SkyCard>
  );
}

function RevokeAllCard() {
  const revokeAll = useRevokeAllMcpGrants();
  const notice = useNotice();
  const error = useInlineError();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const confirmed = typed === MCP_REVOKE_ALL_CONFIRMATION;

  function close() {
    setOpen(false);
    setTyped("");
    error.clear();
  }

  return (
    <SkyCard className="flex flex-col items-start gap-3 p-4">
      <SkyText as="p" size={300} block>
        Ends every AI app connection of every person in every tenant, at once. Each person can
        connect again afterwards. To stop that too, turn AI apps off in Platform settings.
      </SkyText>
      <SkyButton appearance="secondary" testId="ai-apps-revoke-all" onClick={() => setOpen(true)}>
        Disconnect every app
      </SkyButton>
      <SkyDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        onBeforeClose={(reason) => reason !== "primaryAction"}
        size="medium"
        testId="ai-apps-revoke-all-dialog"
        title="Disconnect every AI app?"
        description="Every connection of every person ends now, and each one is recorded in the audit trail."
        primaryAction={{
          label: "Disconnect every app",
          destructive: true,
          disabled: !confirmed,
          loading: revokeAll.isPending,
          onClick: () => {
            error.clear();
            revokeAll.mutate(undefined, {
              onSuccess: (result) => {
                close();
                notice.success(
                  result.revoked === 1
                    ? "1 connection was ended."
                    : `${result.revoked} connections were ended.`,
                );
              },
              onError: (cause) => error.show(cause, "Nothing was disconnected."),
            });
          },
        }}
        secondaryAction={{ label: "Cancel", onClick: close }}
      >
        <div className="flex flex-col gap-3">
          <SkyInput
            label={`Type ${MCP_REVOKE_ALL_CONFIRMATION} to confirm`}
            value={typed}
            autoComplete="off"
            testId="ai-apps-revoke-all-confirm"
            onChange={(event) => setTyped(event.target.value)}
          />
          <InlineErrorBar
            error={error.error}
            title="Nothing was disconnected"
            testId="ai-apps-revoke-all-error"
          />
        </div>
      </SkyDialog>
    </SkyCard>
  );
}

/** This instance's AI apps: its switch and its connections, as far as the person may see them. */
function InstanceSection({
  instanceName,
  instanceSwitch,
  instanceGrants,
  query,
}: {
  instanceName: string;
  instanceSwitch: boolean;
  instanceGrants: boolean;
  query: ReturnType<typeof useInstanceMcpGrants>;
}) {
  return (
    <Section
      title={`This instance: ${instanceName}`}
      intro="AI apps people here have connected, each reading this instance as the person who connected it."
      testId="ai-apps-instance"
    >
      {instanceSwitch ? <InstanceSwitchCard instanceName={instanceName} /> : null}
      {instanceGrants ? (
        <McpGrantListSection
          query={query}
          scope={{ kind: "instance" }}
          showInstance={false}
          showUser
          canRevoke
          ariaLabel="AI apps connected to this instance"
          emptyMessage="Nobody has connected an AI app to this instance."
          testId="ai-apps-instance-grid"
        />
      ) : null}
    </Section>
  );
}

/** This tenant's AI apps: its switch and every connection in it, as far as the person may see them. */
function TenantSection({
  tenantId,
  tenantName,
  tenantSwitch,
  tenantGrants,
  query,
}: {
  tenantId: string;
  tenantName: string;
  tenantSwitch: boolean;
  tenantGrants: boolean;
  query: ReturnType<typeof useTenantMcpGrants>;
}) {
  return (
    <Section
      title={`This tenant: ${tenantName}`}
      intro="Whether AI apps may read this tenant's instances at all, and every connection in it."
      testId="ai-apps-tenant"
    >
      {tenantSwitch ? <TenantSwitchCard tenantId={tenantId} tenantName={tenantName} /> : null}
      {tenantGrants ? (
        <McpGrantListSection
          query={query}
          scope={{ kind: "tenant", tenantId }}
          showInstance
          showUser
          canRevoke
          ariaLabel="AI apps connected in this tenant"
          emptyMessage="Nobody has connected an AI app in this tenant."
          testId="ai-apps-tenant-grid"
        />
      ) : null}
    </Section>
  );
}

export default function AdminAiAppsPage() {
  const { can } = usePermissions();
  const { selectedInstance } = useTenantInstance();
  const instanceSwitch = Boolean(selectedInstance) && can("instance.settings.write");
  const instanceGrants = Boolean(selectedInstance) && can("instance.users.manage");
  const tenantSwitch = Boolean(selectedInstance) && can("tenant.instances.manage");
  const tenantGrants = Boolean(selectedInstance) && can("tenant.users.manage");
  const revokeAll = can("platform.settings.manage");
  const tenantId = selectedInstance?.tenantId ?? null;

  const instanceList = useInstanceMcpGrants(instanceGrants);
  const tenantList = useTenantMcpGrants(tenantId, tenantGrants);

  return (
    <div className="flex flex-col gap-8" data-testid="ai-apps-page">
      {selectedInstance && (instanceSwitch || instanceGrants) ? (
        <InstanceSection
          instanceName={selectedInstance.name}
          instanceSwitch={instanceSwitch}
          instanceGrants={instanceGrants}
          query={instanceList}
        />
      ) : null}

      {selectedInstance && tenantId && (tenantSwitch || tenantGrants) ? (
        <TenantSection
          tenantId={tenantId}
          tenantName={selectedInstance.tenantDisplayName}
          tenantSwitch={tenantSwitch}
          tenantGrants={tenantGrants}
          query={tenantList}
        />
      ) : null}

      {revokeAll ? (
        <Section
          title="Every tenant"
          intro="For an emergency: end every connection on this deployment."
          testId="ai-apps-platform"
        >
          <RevokeAllCard />
        </Section>
      ) : null}
    </div>
  );
}
